import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Search,
  ShoppingBag,
  Plus,
  Minus,
  X,
  ArrowRight,
  Utensils,
  Scissors,
  Store as StoreIcon,
  Clock,
  Home,
  ReceiptText,
  ShoppingCart,
  History,
  CircleHelp,
  UserCircle,
  MessageCircle,
} from "lucide-react";
import * as repository from "@/data/storefrontRepository";
import { formatINR } from "../../../../lib/utils";
import { CartProvider, clearVendorCarts, useCart } from "../../../../lib/cart";
import Modal from "@/shared/components/feedback/Modal";
import Logo from "@/shared/components/branding/Logo";
import ItemImage from "@/shared/components/media/ItemImage";
import { normalizeVendorCapabilities } from "@/domain/vendorCapabilities";
import { resolveTableContext } from "@/domain/publicStoreUrls";
import { activateCustomerSession, getOrCreateCustomerSession, rotateCustomerSession, saveCustomerDetails } from "@/data/customerSessionRepository";
import { getOrdersForCustomerSession } from "@/data/orderRepository";
import { createTableServiceRequest, getRequestsForSession } from "@/data/tableServiceRequestRepository";
import { TABLE_REQUEST_TYPES, normalizeRequestTypes } from "@/domain/tableServiceRequests";
import { sanitizePhoneInput } from "@/shared/validation/phone";
import { toast } from "sonner";
import ThemeToggle from "@/shared/components/controls/ThemeToggle";
import CustomerBottomNavigation from "./components/CustomerBottomNavigation";
import { api, isMockApi } from "@/lib/api";
import { loadPublicRestaurantStorefront } from "@/features/customer/restaurant/publicStorefrontApi";
import { activatePublicCustomerSession, getOrCreatePublicCustomerSession, getStoredPublicCustomerSession, rotatePublicCustomerSession, savePublicCustomerDetails } from "@/features/customer/restaurant/publicCustomerSession";

/**
 * Storefront page — customer-facing. Uses vendor's accent color.
 * Restaurant: cart-based order flow.
 * Salon: pick a service -> booking flow route.
 */
export default function StorefrontPage() {
  const { seedIfNeeded, getVendorBySlug } = repository;
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [vendor, setVendor] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const tableId = searchParams.get("table");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoaded(false);
      setLoadError("");
      try {
        if (isMockApi) {
          seedIfNeeded();
          if (!cancelled) setVendor(getVendorBySlug(slug));
        } else if (!cancelled) {
          setVendor(await loadPublicRestaurantStorefront(slug, { tableId }));
        }
      } catch (error) {
        if (!cancelled) {
          setVendor(null);
          setLoadError(error.message || "This storefront could not be loaded.");
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug, tableId, seedIfNeeded, getVendorBySlug]);

  if (!loaded) return null;

  if (!vendor || !vendor.onboarded) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="text-center max-w-md">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-soft border border-brand/30 grid place-items-center">
            <StoreIcon className="h-6 w-6 text-brand" />
          </div>
          <h1 className="mt-6 text-2xl font-display font-medium">Store not found</h1>
          <p className="mt-2 text-ink-secondary">{loadError || <>The store <span className="font-mono text-brand">/{slug}</span> doesn't exist.</>}</p>
          <Link to="/" className="btn-primary mt-6 inline-flex items-center gap-2">Back home <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    );
  }

  const tableContext = resolveTableContext(vendor, tableId);
  const activeStorefront = tableContext.ok ? (tableContext.storefront || vendor.storefronts?.[0]) : vendor.storefronts?.[0];
  return (
    <CartProvider vendorSlug={vendor.slug} tableId={tableId} taxSettings={activeStorefront?.taxSettings}>
      <StorefrontContent vendor={vendor} />
    </CartProvider>
  );
}

function StorefrontContent({ vendor }) {
  const isRestaurant = vendor.businessType === "restaurant";
  const accentClass = isRestaurant ? "text-restaurant" : "text-salon";
  const accentBg = isRestaurant ? "bg-restaurant" : "bg-salon";
  const accentBgSoft = isRestaurant ? "bg-orange-500/15 border-orange-500/30" : "bg-amber-500/15 border-amber-500/30";

  const list = isRestaurant ? vendor.items : vendor.services;
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [foodType, setFoodType] = useState("all");
  const [selected, setSelected] = useState(null);
  const { items: cartItems, count: cartCount, clear: clearCart } = useCart();
  const [session, setSession] = useState(() => getOrCreateCustomerSession(vendor.id));
  const activeCustomer = !!session.activated;
  const [activationOpen, setActivationOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setViewState] = useState(() => searchParams.get("view") || "home");
  const tableId = searchParams.get("table");
  const tableContext = resolveTableContext(vendor, tableId);
  const capabilities = normalizeVendorCapabilities(vendor.capabilities, vendor.businessType);
  const requestTypes = normalizeRequestTypes(vendor.storefronts?.[0]?.serviceRequestTypes);
  const helpAvailable = isRestaurant && capabilities.tableServiceRequests && tableContext.ok && requestTypes.length > 0;
  const customerTabs = useMemo(() => capabilities.customerOrdering ? [{ id: "home", label: "Home", icon: Home }, { id: "order", label: "My Order", icon: ReceiptText }, { id: "cart", label: "Cart", icon: ShoppingCart }, ...(capabilities.customerHistory ? [{ id: "history", label: "History", icon: History }] : []), { id: "help", label: "Help", icon: CircleHelp }] : [{ id: "home", label: "Home", icon: Home }, { id: "help", label: "Help", icon: CircleHelp }], [capabilities.customerOrdering, capabilities.customerHistory]);
  const setView = (nextView) => {
    const params = new URLSearchParams(searchParams);
    if (nextView === "home") params.delete("view");
    else params.set("view", nextView);
    setViewState(nextView);
    setSearchParams(params);
  };
  useEffect(() => {
    const requestedView = searchParams.get("view") || "home";
    if (requestedView !== view) setViewState(requestedView);
  }, [searchParams, view]);
  useEffect(() => { if (!customerTabs.some(({ id }) => id === view)) setView("home"); }, [customerTabs, view]);

  const filtered = useMemo(
    () =>
      list.filter(
        (it) =>
          it.available &&
          (cat === "all" || it.categoryId === cat) &&
          (!isRestaurant || foodType === "all" || it.foodType === foodType) &&
          (q.trim() === "" || it.name.toLowerCase().includes(q.toLowerCase()))
      ),
    [list, cat, foodType, isRestaurant, q]
  );
  const activateCustomer = async (details) => {
    if (!isMockApi) await activatePublicCustomerSession(vendor.slug, details);
    const next = activateCustomerSession(vendor.id, details);
    setSession(next);
    setActivationOpen(false);
  };
  const signOutCustomer = async () => {
    if (!window.confirm("Sign out on this device? Your previous orders will no longer be visible.")) return;
    try {
      if (!isMockApi) await rotatePublicCustomerSession(vendor.slug);
      const { session: next } = rotateCustomerSession(vendor.id);
      clearCart();
      clearVendorCarts(vendor.slug);
      setSession(next);
      setProfileOpen(false);
      setView("home");
    } catch (error) {
      toast.error(error.message || "Unable to sign out. Please try again.");
    }
  };

  return (
    <div className="min-h-screen">
      {/* Sticky compact header */}
      <header className="sticky top-0 z-30 glass border-b border-white/5">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-bg-surface border border-line overflow-hidden grid place-items-center flex-shrink-0">
              {vendor.logo ? <img src={vendor.logo} alt="" className="h-full w-full object-cover" /> : <StoreIcon className="h-4 w-4 text-ink-muted" />}
            </div>
            <div className="min-w-0">
              <div className="font-display font-medium text-sm truncate">{vendor.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-muted truncate">
                {isRestaurant ? "Restaurant" : "Salon"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isRestaurant && activeCustomer && <button onClick={() => setProfileOpen(true)} className="h-11 w-11 grid place-items-center rounded-full border border-line bg-bg-elevated text-ink-primary focus-visible:ring-2 focus-visible:ring-brand" aria-label="Open profile" data-testid="customer-profile-button"><UserCircle className="h-5 w-5" /></button>}
          </div>
        </div>
      </header>

      {view !== "home" && <CustomerStoreView view={view} vendor={vendor} tableContext={tableContext} requestTypes={helpAvailable ? requestTypes : []} onBrowse={() => setView("home")} />}

      {/* Hero */}
      <section className={view === "home" ? "relative" : "hidden"}>
        <div
          className="h-56 sm:h-72 lg:h-96 bg-cover bg-center bg-bg-elevated"
          style={{ backgroundImage: vendor.coverImage ? `url(${vendor.coverImage})` : undefined }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-bg-base via-bg-base/60 to-black/30" />
        </div>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 -mt-24 sm:-mt-28 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card-surface p-6 sm:p-8"
          >
            <div className="flex items-start gap-5">
              <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-bg-elevated border border-line overflow-hidden grid place-items-center flex-shrink-0">
                {vendor.logo ? <img src={vendor.logo} alt="" className="h-full w-full object-cover" /> : <StoreIcon className="h-6 w-6 text-ink-muted" />}
              </div>
              <div className="min-w-0 flex-1">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${accentBgSoft} ${accentClass}`}>
                  {isRestaurant ? <Utensils className="h-3 w-3" /> : <Scissors className="h-3 w-3" />}
                  {isRestaurant ? "Restaurant" : "Salon"}
                </span>
                <h1 className="mt-3 text-2xl sm:text-4xl font-display font-medium tracking-tighter" data-testid="storefront-name">{vendor.name}</h1>
                {vendor.tagline && <p className="mt-1.5 text-ink-secondary">{vendor.tagline}</p>}
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-muted">
                  {vendor.address && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {vendor.address}</span>}
                  {vendor.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {vendor.phone}</span>}
                </div>
              </div>
            </div>
            {vendor.description && (
              <p className="mt-6 pt-5 border-t border-line text-ink-secondary leading-relaxed">
                {vendor.description}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Catalogue */}
      <section className={view === "home" ? `mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-10 ${cartCount > 0 ? "pb-44" : "pb-32"}` : "hidden"}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className={`text-xs uppercase tracking-widest ${accentClass}`}>{isRestaurant ? "Menu" : "Services"}</div>
            <h2 className="mt-2 text-2xl font-display font-medium tracking-tight">
              {isRestaurant ? "Explore the menu" : "Book a service"}
            </h2>
          </div>
          <div className="relative sm:max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${isRestaurant ? "menu" : "services"}...`}
              className="input-field pl-10"
              data-testid="storefront-search"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-2 mb-6">
          <CatChip active={cat === "all"} onClick={() => setCat("all")} testid="store-cat-all">All</CatChip>
          {vendor.categories.map((c) => (
            <CatChip
              key={c.id}
              active={cat === c.id}
              onClick={() => setCat(c.id)}
              testid={`store-cat-${c.id}`}
            >
              {c.name}
            </CatChip>
          ))}
        </div>
        {isRestaurant && (
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-2 mb-6" role="group" aria-label="Dietary preference" data-testid="food-type-filter">
            <CatChip active={foodType === "all"} onClick={() => setFoodType("all")} testid="food-filter-all">All</CatChip>
            <CatChip active={foodType === "veg"} onClick={() => setFoodType("veg")} testid="food-filter-veg">Veg</CatChip>
            <CatChip active={foodType === "non_veg"} onClick={() => setFoodType("non_veg")} testid="food-filter-non-veg">Non-veg</CatChip>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="card-surface p-16 text-center">
            <div className="font-display text-lg">Nothing to show</div>
            <p className="mt-1 text-ink-secondary text-sm">Try a different search or category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((it, i) => (
              <ItemCard
                key={it.id}
                item={it}
                isRestaurant={isRestaurant}
                orderingAvailable={capabilities.customerOrdering}
                accentClass={accentClass}
                onOpen={() => setSelected(it)}
                onFirstAdd={() => { if (!activeCustomer) setActivationOpen(true); }}
                onBook={(service) => {
                  window.location.href = `/store/${vendor.slug}/book/${service.id}`;
                }}
                index={i}
              />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className={view === "home" ? "border-t border-line mb-20" : "hidden"}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <div className="font-display font-medium">{vendor.name}</div>
            <div className="text-xs text-ink-muted mt-1">Powered by Digital Indian Market</div>
          </div>
          <Logo size="sm" />
        </div>
      </footer>

      {/* Product/service modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)}>
        {selected && (
          <ItemDetail
            item={selected}
            isRestaurant={isRestaurant}
            orderingAvailable={capabilities.customerOrdering}
            accentClass={accentClass}
            accentBg={accentBg}
            vendor={vendor}
            onFirstAdd={() => { if (!activeCustomer) setActivationOpen(true); }}
            onClose={() => setSelected(null)}
          />
        )}
      </Modal>

      {isRestaurant && capabilities.customerOrdering && cartCount > 0 && view !== "cart" && <button onClick={() => activeCustomer ? setView("cart") : setActivationOpen(true)} className="fixed z-30 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] left-3 right-3 mx-auto max-w-lg card-surface p-3 flex items-center gap-3 shadow-xl border-brand/30 text-left" data-testid="floating-cart-summary"><ItemImage src={cartItems[0]?.image} name={cartItems[0]?.name} className="h-11 w-11 rounded-lg object-cover shrink-0" /><span className="min-w-0 flex-1"><span className="block text-sm font-medium">{cartCount} item{cartCount === 1 ? "" : "s"}</span><span className="block text-xs text-ink-secondary">Ready when you are</span></span><span className="btn-primary !py-2 !px-3 text-sm">View cart</span></button>}
      <ActivationSheet open={activationOpen} onClose={() => setActivationOpen(false)} vendor={vendor} onActivated={activateCustomer} />
      <ProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} vendor={vendor} onSaved={setSession} onSignOut={signOutCustomer} />
      {isRestaurant && activeCustomer && <CustomerBottomNavigation items={customerTabs} activeView={view} cartCount={cartCount} onSelect={setView} />}
    </div>
  );
}

function CustomerStoreView({ view, vendor, tableContext, requestTypes, onBrowse }) {
  const session = getOrCreateCustomerSession(vendor.id);
  const [tick, setTick] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [serverOrders, setServerOrders] = useState([]);
  const [serverRequests, setServerRequests] = useState([]);
  useEffect(() => {
    if (isMockApi) return undefined;
    const serverSession = getStoredPublicCustomerSession(vendor.slug);
    if (!serverSession?.id) { setServerOrders([]); return undefined; }
    let cancelled = false;
    api.listPublicCustomerOrders(vendor.slug, serverSession.id)
      .then((orders) => {
        if (!cancelled) setServerOrders(orders.map((order) => ({
          ...order,
          code: order.orderNumber,
          createdAt: order.createdAt,
          subtotal: order.subtotalAmount,
          tax: order.taxAmount,
          total: order.totalAmount,
          items: [],
          itemCount: order.itemCount || 0,
          customer: { name: order.customer?.name || "Guest", phone: order.customer?.mobile || "", address: order.deliveryAddress || "" },
        })));
      })
      .catch(() => { if (!cancelled) setServerOrders([]); });
    return () => { cancelled = true; };
  }, [vendor.slug, tick]);
  useEffect(() => {
    if (isMockApi) return undefined;
    const serverSession = getStoredPublicCustomerSession(vendor.slug);
    if (!serverSession?.id) { setServerRequests([]); return undefined; }
    let cancelled = false;
    api.listPublicCustomerServiceRequests(vendor.slug, serverSession.id)
      .then((requests) => { if (!cancelled) setServerRequests(requests); })
      .catch(() => { if (!cancelled) setServerRequests([]); });
    return () => { cancelled = true; };
  }, [vendor.slug, tick]);
  const orders = isMockApi ? getOrdersForCustomerSession(vendor.id, session.id) : serverOrders;
  const active = orders.filter((order) => !["completed", "cancelled"].includes(order.status));
  // Device history is a complete customer journey, not only terminal orders.
  // Keeping it session-scoped preserves the existing privacy boundary.
  const history = orders;
  const requests = isMockApi ? getRequestsForSession(vendor.id, session.id) : serverRequests;
  if (view === "cart") return <CustomerCartView vendor={vendor} onBrowse={onBrowse} />;
  if (view === "order") return <CustomerPanel title="My Order">{active.length ? <div className="space-y-3">{active.map((order) => <OrderSummary key={order.id} order={order} />)}</div> : <EmptyState text="No active order on this device." action="Start an order" onBrowse={onBrowse} />}</CustomerPanel>;
  if (view === "history") return <CustomerPanel title="Orders on this device">{history.length ? <><ul className="space-y-3">{history.map((order) => <li key={order.id}><button onClick={() => setSelectedOrder(order)} className="card-surface w-full min-h-[88px] p-4 text-left flex justify-between gap-3" data-testid={`history-order-${order.id}`}><span><span className="block font-mono">{order.code}</span><span className="block mt-1 text-xs text-ink-secondary">{new Date(order.createdAt).toLocaleDateString()} · {order.items.slice(0, 2).map(({ name }) => name).join(", ") || `${order.itemCount || 0} item${order.itemCount === 1 ? "" : "s"}`}</span></span><span className="text-right"><span className="block font-mono">{formatINR(order.total)}</span><span className="block mt-1 text-xs capitalize">{order.status}</span></span></button></li>)}</ul><Modal open={!!selectedOrder} onClose={() => setSelectedOrder(null)} labelledBy="history-order-title"><div data-testid="history-order-detail"><h2 id="history-order-title" className="font-display text-2xl mb-4">Order details</h2>{selectedOrder && <OrderSummary order={selectedOrder} />}</div></Modal></> : <div className="card-surface p-8 text-center">Your completed orders will appear here.</div>}</CustomerPanel>;
  if (view === "help") { const functional = tableContext.ok && tableContext.table && requestTypes.length > 0; return <CustomerPanel title={functional ? "Help at your table" : "Contact restaurant"}>{functional ? <><p className="text-sm text-ink-secondary mb-4">Requests for <strong>{tableContext.table.label}</strong></p><div className="grid gap-2">{TABLE_REQUEST_TYPES.filter(({ id }) => requestTypes.includes(id)).map((type) => { const current = requests.find((request) => request.type === type.id && request.status !== "completed"); return <button key={type.id} className="btn-ghost min-h-[44px] flex justify-between" onClick={async () => { try { const result = isMockApi ? createTableServiceRequest({ vendorId: vendor.id, storefrontId: tableContext.storefront.id, tableId: tableContext.table.id, customerSessionId: session.id, type: type.id }) : await api.createPublicServiceRequest(vendor.slug, (await getOrCreatePublicCustomerSession(vendor.slug)).id, { tableId: tableContext.table.id, type: type.id }); toast.success(result.duplicate ? "Already requested" : "Request sent"); setTick((value) => value + 1); } catch (error) { toast.error(error.message || "Could not send request. Please try again."); } }} data-testid={`customer-help-${type.id}`}><span>{type.label}</span><span className="capitalize text-xs">{current?.status || "Request"}</span></button>; })}</div></> : vendor.phone ? <div className="card-surface p-5"><p className="text-sm text-ink-secondary">Call or message {vendor.name} directly.</p><div className="mt-4 flex flex-wrap gap-2"><a href={`tel:${vendor.phone}`} className="btn-primary min-h-[44px] inline-flex items-center gap-2"><Phone className="h-4 w-4" /> Call</a><a href={`https://wa.me/${vendor.phone.replace(/\D/g, "")}`} className="btn-ghost min-h-[44px] inline-flex items-center gap-2"><MessageCircle className="h-4 w-4" /> WhatsApp</a></div></div> : <div className="card-surface p-6 text-center">Contact options are not available right now. Please speak with restaurant staff.</div>}</CustomerPanel>; }
  return null;
}
function CustomerCartView({ vendor, onBrowse }) { const { items, increment, decrement, remove, subtotal, tax, total, taxSettings } = useCart(); const nav = useNavigate(); return <CustomerPanel title="Your cart">{items.length ? <div className="space-y-4"><ul className="space-y-2">{items.map((item) => <li key={item.lineId || item.id} className="card-surface p-3 flex items-center gap-3"><ItemImage src={item.image} name={item.name} className="h-14 w-14 rounded-lg object-cover" /><div className="min-w-0 flex-1"><div className="font-medium truncate">{item.name}</div>{item.sizeLabel && <div className="text-xs text-ink-secondary capitalize">{item.sizeLabel}</div>}<div className="text-sm font-mono">{formatINR(item.price)}</div></div><div className="flex items-center gap-1"><button className="h-11 w-11" onClick={() => decrement(item.lineId || `${item.id}:base`)} aria-label={`Decrease ${item.name}`}><Minus className="mx-auto h-4 w-4" /></button><span>{item.qty}</span><button className="h-11 w-11" onClick={() => increment(item.lineId || `${item.id}:base`)} aria-label={`Increase ${item.name}`}><Plus className="mx-auto h-4 w-4" /></button><button className="h-11 w-11 text-red-500" onClick={() => remove(item.lineId || `${item.id}:base`)} aria-label={`Remove ${item.name}`}><X className="mx-auto h-4 w-4" /></button></div></li>)}</ul><div className="card-surface p-5 space-y-2"><div className="flex justify-between"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>{taxSettings.enabled && taxSettings.showTax !== false && <div className="flex justify-between"><span>Tax ({taxSettings.rate}%)</span><span>{formatINR(tax)}</span></div>}<div className="flex justify-between text-lg font-semibold"><span>Total</span><span>{formatINR(total)}</span></div><button onClick={() => nav(`/store/${vendor.slug}/checkout${window.location.search}`)} className="btn-primary min-h-[44px] w-full mt-3" data-testid="customer-cart-checkout">Continue to checkout</button></div></div> : <EmptyState text="Your cart is empty." action="Browse items" onBrowse={onBrowse} />}</CustomerPanel>; }

function ActivationSheet({ open, onClose, vendor, onActivated }) { const [details, setDetails] = useState({ name: "", phone: "" }); const [error, setError] = useState(""); const [saving, setSaving] = useState(false); const submit = async () => { setSaving(true); try { await onActivated(details); setError(""); } catch (reason) { setError(reason.message); } finally { setSaving(false); } }; return <Modal open={open} onClose={onClose} labelledBy="customer-activation-title" className="sm:!max-w-md"><div data-testid="customer-activation-sheet"><h2 id="customer-activation-title" className="font-display text-2xl">Tell us who the order is for</h2><p className="mt-1 text-sm text-ink-secondary">Saved for this device at {vendor.name}.</p><div className="mt-5 space-y-3"><label className="block text-sm">Name<input autoFocus className="input-field mt-1" value={details.name} onChange={(event) => setDetails({ ...details, name: event.target.value })} data-testid="customer-activation-name" /></label><label className="block text-sm">Phone (optional)<input className="input-field mt-1" inputMode="tel" value={details.phone} onChange={(event) => setDetails({ ...details, phone: sanitizePhoneInput(event.target.value) })} data-testid="customer-activation-phone" /></label>{error && <p role="alert" className="text-sm text-red-600">{error}</p>}<div className="grid grid-cols-2 gap-2"><button className="btn-ghost min-h-[44px]" onClick={onClose}>Not now</button><button className="btn-primary min-h-[44px]" onClick={submit} disabled={saving} data-testid="customer-activation-continue">{saving ? "Saving…" : "Continue"}</button></div></div></div></Modal>; }

function ProfileSheet({ open, onClose, vendor, onSaved, onSignOut }) { const session = getOrCreateCustomerSession(vendor.id); const [details, setDetails] = useState({ name: session.name || "", phone: session.phone || "" }); useEffect(() => { if (open) { const current = getOrCreateCustomerSession(vendor.id); setDetails({ name: current.name || "", phone: current.phone || "" }); } }, [open, vendor.id]); const save = async () => { try { if (!isMockApi) await savePublicCustomerDetails(vendor.slug, details); const saved = saveCustomerDetails(vendor.id, details); setDetails({ name: saved.name, phone: saved.phone }); onSaved?.(saved); toast.success("Details updated"); } catch (error) { toast.error(error.message); } }; return <Modal open={open} onClose={onClose} labelledBy="customer-profile-title" className="sm:!max-w-lg"><div data-testid="customer-profile-sheet"><h2 id="customer-profile-title" className="font-display text-2xl">Your profile</h2><p className="mt-1 text-sm text-ink-secondary">Saved for this device at {vendor.name}.</p><div className="mt-5 space-y-3"><label className="block text-sm">Name<input className="input-field mt-1" value={details.name} onChange={(event) => setDetails({ ...details, name: event.target.value })} data-testid="customer-details-name" /></label><label className="block text-sm">Phone (optional)<input className="input-field mt-1" inputMode="tel" value={details.phone} onChange={(event) => setDetails({ ...details, phone: sanitizePhoneInput(event.target.value) })} data-testid="customer-details-phone" /></label><button className="btn-primary min-h-[44px] w-full" onClick={save} data-testid="customer-details-save">Update details</button><section className="border-t border-line pt-4"><h3 className="text-sm font-medium mb-2">Appearance</h3><ThemeToggle testid="customer-profile-theme" /></section><section className="border-t border-line pt-4"><h3 className="font-display text-lg">About this restaurant</h3><p className="mt-1 text-sm text-ink-secondary">{vendor.description || vendor.tagline || vendor.name}</p>{vendor.phone && <div className="mt-3 flex gap-2"><a href={`tel:${vendor.phone}`} className="btn-ghost min-h-[44px] inline-flex items-center gap-2"><Phone className="h-4 w-4" /> Call</a><a href={`https://wa.me/${vendor.phone.replace(/\D/g, "")}`} className="btn-ghost min-h-[44px] inline-flex items-center gap-2"><MessageCircle className="h-4 w-4" /> WhatsApp</a></div>}</section><section className="border-t border-line pt-4"><p className="text-xs text-ink-secondary">Signing out removes your details and basket from this device. Existing orders are not deleted.</p><button className="btn-ghost min-h-[44px] w-full mt-2 text-red-600" onClick={onSignOut} data-testid="customer-sign-out">Sign out</button></section></div></div></Modal>; }

function CustomerPanel({ title, children }) { return <main className="mx-auto max-w-3xl min-h-[calc(100dvh-7rem)] px-4 py-8 pb-28" data-testid="customer-store-view"><h1 className="font-display text-2xl mb-5">{title}</h1>{children}</main>; }
function EmptyState({ text, action = "Browse items", onBrowse }) { return <div className="card-surface p-8 text-center"><p>{text}</p><button className="btn-primary mt-4 min-h-[44px]" onClick={onBrowse}>{action}</button></div>; }
function OrderSummary({ order }) { const steps = ["new", "preparing", "ready", "completed"]; const current = Math.max(0, steps.indexOf(order.status)); return <article className="card-surface p-5 overflow-hidden" data-testid={`customer-order-${order.id}`}><div className="flex justify-between gap-3"><div><div className="text-xs uppercase tracking-widest text-ink-muted">Order</div><span className="font-mono text-lg">{order.code}</span></div><div className="text-right"><span className="block capitalize font-semibold">{order.status}</span><span className="font-mono">{formatINR(order.total)}</span></div></div>{order.tableLabel && <p className="mt-2 text-sm font-medium">{order.tableLabel}</p>}<p className="mt-1 text-xs text-ink-muted">Placed {new Date(order.createdAt).toLocaleString()}</p><ul className="mt-4 divide-y divide-line">{order.items.map((item) => <li key={item.id} className="py-2 flex justify-between text-sm"><span>{item.name}{item.sizeLabel && <span className="capitalize text-ink-secondary"> · {item.sizeLabel}</span>} ×{item.qty}</span><span>{formatINR(item.price * item.qty)}</span></li>)}</ul>{order.status === "cancelled" ? <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm">This order was cancelled.</div> : <ol className="mt-5 grid grid-cols-4 gap-1" aria-label="Order progress" data-testid="customer-order-timeline">{["Received", "Preparing", "Ready", "Completed"].map((label, index) => <li key={label} data-state={index < current ? "completed" : index === current ? "current" : "future"} className={`min-w-0 border-t-4 pt-2 text-[10px] sm:text-xs ${index <= current ? "border-brand text-ink-primary font-semibold" : "border-line text-ink-muted"}`}><span className="block truncate">{label}</span><span className="sr-only">{index < current ? "Completed" : index === current ? "Current" : "Upcoming"}</span></li>)}</ol>}</article>; }

function CatChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-shrink-0 px-4 py-2 rounded-full text-sm border transition-colors whitespace-nowrap ${
        active
          ? "bg-white/10 text-ink-primary border-white/20"
          : "bg-bg-surface text-ink-secondary border-line hover:text-ink-primary hover:border-white/20"
      }`}
    >
      {children}
    </button>
  );
}

function ItemCard({ item, isRestaurant, orderingAvailable, accentClass, onOpen, onFirstAdd, onBook, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.03 }}
      className="card-surface card-hover overflow-hidden flex flex-col"
      data-testid={`store-item-${item.id}`}
    >
      <button onClick={onOpen} className="relative aspect-[4/3] w-full bg-bg-elevated overflow-hidden text-left">
        <ItemImage src={item.image} name={item.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-105" fallbackClassName="text-2xl" />
      </button>
      <div className="p-4 flex-1 flex flex-col">
        <button onClick={onOpen} className="text-left">
          <div className="font-display font-medium">{item.name}</div>
          {isRestaurant && item.foodType && <div className={`mt-1 text-[11px] font-medium ${item.foodType === "veg" ? "text-emerald-400" : "text-red-400"}`}>{item.foodType === "veg" ? "● Veg" : "● Non-veg"}</div>}
          <div className="mt-1 text-xs text-ink-secondary line-clamp-2 min-h-[2.25rem]">{item.description}</div>
        </button>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className={`font-mono text-sm ${accentClass}`}>{item.sizes?.length ? `From ${formatINR(Math.min(...item.sizes.map((size) => Number(size.price))))}` : formatINR(item.price)}</div>
            {!isRestaurant && (
              <div className="text-[11px] text-ink-muted inline-flex items-center gap-1 mt-0.5">
                <Clock className="h-3 w-3" /> {item.duration}m
              </div>
            )}
          </div>
          {isRestaurant && orderingAvailable ? (
            <AddButton item={item} onFirstAdd={onFirstAdd} />
          ) : isRestaurant ? <span className="text-xs text-ink-muted">Ordering unavailable</span> : (
            <button
              onClick={() => onBook(item)}
              className="btn-primary !py-2 !px-3 text-sm inline-flex items-center gap-1.5"
              data-testid={`book-service-${item.id}`}
            >
              Book <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function AddButton({ item, onFirstAdd }) {
  const { items, add, increment, decrement } = useCart();
  const [portionOpen, setPortionOpen] = useState(false);
  const line = items.find((i) => i.id === item.id && !i.menuItemSizeId);
  if (item.sizes?.length) {
    return <><button type="button" onClick={() => setPortionOpen(true)} className="btn-primary !py-2 !px-3 text-sm inline-flex items-center gap-1.5" data-testid={`add-item-${item.id}`}><Plus className="h-3.5 w-3.5" /> Add</button><Modal open={portionOpen} onClose={() => setPortionOpen(false)} labelledBy={`portion-picker-title-${item.id}`} className="sm:!max-w-md"><div data-testid={`add-size-options-${item.id}`}><h2 id={`portion-picker-title-${item.id}`} className="font-display text-2xl">Choose a portion</h2><p className="mt-1 text-sm text-ink-secondary">Select a size for {item.name}.</p><div className="mt-5 grid grid-cols-1 gap-2">{item.sizes.filter((size) => size.isAvailable !== false).map((size) => <button key={size.id} type="button" onClick={() => { add({ ...item, price: size.price, menuItemSizeId: size.id, sizeLabel: size.label }); onFirstAdd?.(); setPortionOpen(false); }} className="card-surface min-h-12 w-full px-4 py-3 text-left flex items-center justify-between capitalize hover:border-brand/50 focus-visible:ring-2 focus-visible:ring-brand" data-testid={`add-size-${item.id}-${size.id}`}><span className="font-medium">{size.label}</span><span className="font-mono text-brand">{formatINR(size.price)}</span></button>)}</div><button type="button" onClick={() => setPortionOpen(false)} className="btn-ghost mt-4 min-h-11 w-full">Cancel</button></div></Modal></>;
  }
  if (!line) {
    return (
      <button onClick={() => { add(item); onFirstAdd?.(); }} className="btn-primary !py-2 !px-3 text-sm inline-flex items-center gap-1.5" data-testid={`add-item-${item.id}`}>
        <Plus className="h-3.5 w-3.5" /> Add
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-line bg-bg-elevated pl-1 pr-1 py-0.5">
      <button onClick={() => decrement(line.lineId || `${item.id}:base`)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/5 text-ink-secondary hover:text-ink-primary transition-colors" data-testid={`decrement-${item.id}`}>
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="text-sm font-mono min-w-[1rem] text-center">{line.qty}</span>
      <button onClick={() => increment(line.lineId || `${item.id}:base`)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/5 text-ink-secondary hover:text-ink-primary transition-colors" data-testid={`increment-${item.id}`}>
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ItemDetail({ item, isRestaurant, orderingAvailable, accentClass, accentBg, vendor, onClose, onFirstAdd }) {
  const selectableSizes = (item.sizes || []).filter((size) => size.isAvailable !== false);
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const selectedSize = selectableSizes.find((size) => size.id === selectedSizeId) || null;
  return (
    <div>
      <div className="rounded-xl overflow-hidden bg-bg-elevated -mt-2 mb-5 aspect-[16/9]"><ItemImage src={item.image} name={item.name} className="h-full w-full object-cover" fallbackClassName="text-2xl" /></div>
      <h2 className="font-display text-2xl font-medium">{item.name}</h2>
      <div className={`mt-1 font-mono ${accentClass}`}>{formatINR(selectedSize?.price ?? item.price)}{!isRestaurant && ` · ${item.duration}m`}</div>
      {item.description && <p className="mt-4 text-ink-secondary leading-relaxed">{item.description}</p>}
      {isRestaurant && selectableSizes.length > 0 && (
        <fieldset className="mt-5" data-testid={`size-picker-${item.id}`}>
          <legend className="text-sm font-medium">Choose a portion</legend>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {selectableSizes.map((size) => <button key={size.id} type="button" onClick={() => setSelectedSizeId(size.id)} className={`min-h-[44px] rounded-lg border px-2 text-sm capitalize ${selectedSizeId === size.id ? "border-brand bg-brand-soft text-ink-primary" : "border-line bg-bg-elevated text-ink-secondary"}`} aria-pressed={selectedSizeId === size.id} data-testid={`size-option-${size.id}`}>{size.label}<span className="block font-mono text-xs">{formatINR(size.price)}</span></button>)}
          </div>
        </fieldset>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <button onClick={onClose} className="btn-ghost">Close</button>
        {isRestaurant && orderingAvailable ? (
          <DetailAddBtn item={item} selectedSize={selectedSize} sizeRequired={selectableSizes.length > 0} onDone={onClose} onFirstAdd={onFirstAdd} />
        ) : isRestaurant ? <span className="text-sm text-ink-muted">Customer ordering is unavailable.</span> : (
          <Link
            to={`/store/${vendor.slug}/book/${item.id}`}
            className="btn-primary inline-flex items-center gap-2"
            data-testid={`detail-book-${item.id}`}
          >
            Book this service <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

function DetailAddBtn({ item, selectedSize, sizeRequired, onDone, onFirstAdd }) {
  const { add } = useCart();
  return (
    <button
      onClick={() => {
        if (sizeRequired && !selectedSize) return toast.error("Choose a portion first");
        add(selectedSize ? { ...item, price: selectedSize.price, menuItemSizeId: selectedSize.id, sizeLabel: selectedSize.label } : item);
        onFirstAdd?.();
        onDone?.();
      }}
      className="btn-primary inline-flex items-center gap-2"
      data-testid={`detail-add-${item.id}`}
    >
      <Plus className="h-4 w-4" /> {sizeRequired && !selectedSize ? "Choose portion" : "Add to cart"}
    </button>
  );
}

function CartTrigger({ onOpen, accentBg }) {
  const { count } = useCart();
  return (
    <button
      onClick={onOpen}
      className={`relative inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium text-white ${accentBg} transition-transform hover:-translate-y-0.5`}
      data-testid="cart-trigger-btn"
    >
      <ShoppingBag className="h-4 w-4" />
      <span className="hidden sm:inline">Cart</span>
      {count > 0 && (
        <span className="min-w-[20px] h-5 rounded-full bg-white text-black text-[11px] font-semibold grid place-items-center px-1.5" data-testid="cart-count">
          {count}
        </span>
      )}
    </button>
  );
}

function CartDrawer({ open, onClose, vendor, accentBg, accentClass }) {
  const { items, increment, decrement, remove, subtotal, tax, total, taxSettings } = useCart();
  const nav = useNavigate();

  return (
    <div
      className={`fixed inset-0 z-50 pointer-events-none transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0"}`}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside
        className={`absolute right-0 top-0 h-full w-full sm:w-[420px] bg-bg-surface border-l border-line flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="cart-drawer"
      >
        <div className="p-5 border-b border-line flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted">Cart</div>
            <div className="mt-1 font-display text-xl font-medium">Your order</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-white/5 text-ink-secondary hover:text-ink-primary transition-colors" data-testid="cart-close-btn">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {items.length === 0 ? (
            <div className="h-full grid place-items-center text-center">
              <div>
                <ShoppingBag className="h-8 w-8 text-ink-muted mx-auto" />
                <div className="mt-3 font-display">Your cart is empty</div>
                <p className="mt-1 text-sm text-ink-secondary">Add items from the menu to get started.</p>
              </div>
            </div>
          ) : (
            items.map((line) => (
              <div key={line.lineId || line.id} className="rounded-xl bg-bg-elevated border border-line p-3 flex gap-3">
                <div className="h-14 w-14 rounded-lg bg-bg-surface border border-line overflow-hidden flex-shrink-0 grid place-items-center">
                  <ItemImage src={line.image} name={line.name} className="h-full w-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium truncate">{line.name}</div>
                    <button onClick={() => remove(line.lineId || `${line.id}:base`)} className="text-ink-muted hover:text-red-400 transition-colors" aria-label="Remove" data-testid={`cart-remove-${line.id}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="text-xs text-ink-secondary capitalize">{line.sizeLabel || ""}</div>
                  <div className={`text-xs mt-0.5 ${accentClass}`}>{formatINR(line.price)}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="inline-flex items-center gap-1 rounded-full border border-line bg-bg-surface">
                      <button onClick={() => decrement(line.lineId || `${line.id}:base`)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/5 text-ink-secondary" data-testid={`cart-decrement-${line.id}`}>
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="text-sm font-mono min-w-[1.25rem] text-center">{line.qty}</span>
                      <button onClick={() => increment(line.lineId || `${line.id}:base`)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/5 text-ink-secondary" data-testid={`cart-increment-${line.id}`}>
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="font-mono text-sm">{formatINR(line.price * line.qty)}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-line p-5 space-y-3">
            <Row label="Subtotal" value={formatINR(subtotal)} />
            {taxSettings?.enabled && taxSettings?.showTax !== false && <Row label={`Tax (${taxSettings.rate}%)`} value={formatINR(tax)} />}
            <Row label="Total" value={formatINR(total)} bold />
            <button
              onClick={() => {
                onClose();
                nav(`/store/${vendor.slug}/checkout${window.location.search}`);
              }}
              className={`w-full text-white font-medium py-3 rounded-xl inline-flex items-center justify-center gap-2 ${accentBg} transition-transform hover:-translate-y-0.5`}
              data-testid="cart-checkout-btn"
            >
              Checkout <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "text-ink-primary font-medium" : "text-ink-secondary text-sm"}>{label}</span>
      <span className={`font-mono ${bold ? "text-lg font-semibold" : "text-sm"}`}>{value}</span>
    </div>
  );
}
